import classNames from 'classnames';
import omitBy from 'lodash/omitBy';
import uniqBy from 'lodash/uniqBy';
import React, { useMemo, useEffect, useState } from 'react';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString } from '../../../util/routes';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';
import omit from 'lodash/omit';

import { FormattedMessage } from '../../../util/reactIntl';
import css from './MainPanelHeader.module.css';
import Icons from '../../../components/Icons/Icons';
import { InlineTextButton } from '../../../components';

// --- helpers ---
const isEmptyValue = v =>
  v == null ||
  v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

// Turn "a,b,c" into ["a","b","c"]
const toArrayValue = value => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    // If backend encodes multiselect as comma-separated string
    return value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  // number / boolean etc.
  return [value];
};

// Map url param key to config key (pub_spirit_type -> spirit_type)
const toConfigKey = urlKey => {
  if (!urlKey) return urlKey;
  return urlKey.startsWith('pub_') ? urlKey.replace(/^pub_/, '') : urlKey;
};

const cleanSelectedFilters = selectedFilters => omitBy(selectedFilters || {}, isEmptyValue);

const getOptionLabel = (filterConfig, rawValue) => {
  const opts = filterConfig?.enumOptions || filterConfig?.options || [];
  const found = opts.find(o => `${o.option ?? o.key ?? o.id ?? o.value}` === `${rawValue}`);
  return found?.label ?? found?.optionLabel ?? found?.text ?? `${rawValue}`;
};

const getCategoryLabel = (listingCategories, rawValue) => {
  const categories = Array.isArray(listingCategories) ? listingCategories : [];
  const found = categories.find(c => `${c.id}` === `${rawValue}` || `${c.key}` === `${rawValue}`);
  return found?.name || found?.label || `${rawValue}`;
};

const buildAppliedPills = ({ selectedFilters, availableFilters, listingCategories }) => {
  const cleaned = cleanSelectedFilters(selectedFilters);

  const IGNORE = new Set(['page', 'mapSearch', 'origin', 'bounds', 'address', 'keywords', 'sort']);

  // availableFilters keys are like "spirit_type", "producer"
  const filtersByKey = new Map((availableFilters || []).map(f => [f.key, f]));

  const pills = [];

  Object.entries(cleaned).forEach(([urlKey, rawValue]) => {
    if (IGNORE.has(urlKey)) return;

    const configKey = toConfigKey(urlKey);
    const filterConfig = filtersByKey.get(configKey);

    const values = toArrayValue(rawValue);

    values.forEach(v => {
      const label =
        configKey === 'categoryLevel'
          ? getCategoryLabel(listingCategories, v)
          : filterConfig
          ? getOptionLabel(filterConfig, v)
          : `${v}`;

      pills.push({
        // Keep BOTH keys:
        // - urlKey is needed to remove filter from URL
        // - configKey is used to resolve labels
        urlKey,
        configKey,
        value: v,
        label,
      });
    });
  });

  return uniqBy(pills, p => `${p.urlKey}:${p.value}`);
};

const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    resultsCount,
    searchInProgress = false,
    noResultsInfo,
routeConfiguration,
    selectedFilters,
    availableFilters,
    listingCategories,
    onClearAll,
    onRemoveFilter,
    history,
    location
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const appliedPills = useMemo(() => {
    return buildAppliedPills({ selectedFilters, availableFilters, listingCategories });
  }, [selectedFilters, availableFilters, listingCategories]);


   // Read current keywords from URL
  const urlParams = useMemo(() => parse(location?.search || ''), [location?.search]);
  const currentKeywords = urlParams?.keywords || '';

  const [keywordValue, setKeywordValue] = useState(currentKeywords);

  // Keep input synced if URL changes from elsewhere (filters, back/forward)
  useEffect(() => {
    setKeywordValue(currentKeywords);
  }, [currentKeywords]);

  // Debounce pushing to URL
  useEffect(() => {
    const t = setTimeout(() => {
      if (!history || !routeConfiguration || !location) return;

      // Only push if changed
      if ((currentKeywords || '') === (keywordValue || '')) return;

      const nextParams =
        keywordValue && keywordValue.trim().length > 0
          ? { ...urlParams, keywords: keywordValue.trim() }
          : omit(urlParams, 'keywords');

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
        routeConfiguration,
        location
      );

      history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, nextParams));
    }, 350);

    return () => clearTimeout(t);
  }, [keywordValue, history, routeConfiguration, location]);

  return (
    <div className={classes}>
      <div className={css.searchOptions}>
        <h1 className={css.searchResultSummary}>
          <strong className={css.resultsFound}>
            {searchInProgress ? (
              <FormattedMessage id="MainPanelHeader.loadingResults" />
            ) : (
              <FormattedMessage id="MainPanelHeader.foundResults" values={{ count: resultsCount }} />
            )}
          </strong>
        </h1>

        {isSortByActive ? (
          <div className={css.sortyByWrapper}>
            <span className={css.sortyBy}>
              <FormattedMessage id="MainPanelHeader.sortBy" />
            </span>
            {sortByComponent}
          </div>
        ) : null}
      </div>

      <div className={css.filterSearchSec}>
        <div className={css.appliedFilters}>
          {appliedPills.map(pill => (
            <span key={`${pill.urlKey}-${pill.value}`} className={css.pill}>
              {pill.label}{' '}
              <span
                className={css.delete}
                role="button"
                tabIndex={0}
                onClick={() => onRemoveFilter?.(pill.urlKey, pill.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') onRemoveFilter?.(pill.urlKey, pill.value);
                }}
              >
                <Icons name="cross" />
              </span>
            </span>
          ))}

          {appliedPills.length > 0 ? (
            <InlineTextButton className={css.clear} onClick={onClearAll}>
              Clear
            </InlineTextButton>
          ) : null}
        </div>

        <div className={css.searchContainer}>
          <Icons name="search" />
          <input
            value={keywordValue}
            onChange={e => setKeywordValue(e.target.value)}
            placeholder="Search by product name, producer or barrel ID..."
          />
          {/* {keywordValue ? (
            <button
              type="button"
              className={css.clearSearchBtn}
              onClick={() => setKeywordValue('')}
              aria-label="Clear search"
            >
              <Icons name="cross" />
            </button>
          ) : null} */}
        </div>
      </div>

      {children}
      {noResultsInfo ? noResultsInfo : null}
    </div>
  );
};

export default MainPanelHeader;
